import axios from 'axios';

import attachAuthToken from './axiosConfig';

/**
 * Instância própria com o interceptor padrão do projeto.
 *
 * ⚠️ `attachAuthToken` é o que dá o **refresh automático no 401**, e aqui isso não é conforto: o
 * access token vale 2h e o inspetor passa a manhã inteira na rua. Sem ele, a primeira
 * sincronização depois do token expirar cairia em 401 — que a fila trata como pendente (nada se
 * perde), mas o inspetor veria "pendentes" sem motivo aparente até relogar.
 */
const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL });
attachAuthToken(api);

/**
 * Oficina Externa (at-114, ADR 0059).
 *
 * 🔴 NENHUMA função aqui tem `catch`. Quem trata o erro é a tela, e neste módulo a diferença
 * importa mais que na média do sistema: "não há veículo nesta oficina" e "não consegui perguntar"
 * levam o inspetor a conclusões opostas — no primeiro caso ele segue viagem, no segundo ele
 * precisa saber que o aparelho está sem dados (regra da at-60).
 */

export type ChecklistVisitaTipo =
  | 'SIM_NAO'
  | 'ESCOLHA_UNICA'
  | 'ESCOLHA_MULTIPLA'
  | 'TEXTO'
  | 'NUMERO'
  | 'DATA';

export interface RegiaoExterna {
  id: string;
  nome: string;
  ordem: number;
  ativa?: boolean;
  totalOficinas?: number;
}

export interface OficinaExterna {
  id: string;
  nome: string;
  regiaoId?: string;
  regiao?: { id: string; nome: string; ativa?: boolean };
  endereco?: string | null;
  telefone?: string | null;
  contato?: string | null;
  observacoes?: string | null;
  ativa?: boolean;
  atendimentosAtivos?: number;
}

export interface EtapaExterna {
  id: string;
  nome: string;
  ordem: number;
  ativa?: boolean;
}

export interface ChecklistVisitaItem {
  id: string;
  rotulo: string;
  tipo: ChecklistVisitaTipo;
  obrigatorio: boolean;
  opcoes: string[];
  ordem: number;
  ativo: boolean;
}

export interface ChecklistVisita {
  versao: string;
  itens: ChecklistVisitaItem[];
}

export interface AtendimentoExterno {
  id: string;
  placa: string;
  classe: string;
  modelo?: string | null;
  cor?: string | null;
  anoModelo?: string | null;
  entradaEm: string;
  saidaEm?: string | null;
  saidaMotivo?: string | null;
  /** Entrega TOTAL do veículo, como a oficina informou na última visita. */
  previsaoEntrega?: string | null;
  /** Término da ETAPA atual, como a oficina informou na última visita (25/09/2026). */
  previsaoEtapa?: string | null;
  ultimaVisitaEm?: string | null;
  diasSemVisita: number | null;
  totalVisitas?: number;
  evento: { id: string; eventNumber: number };
  oficina: { id: string; nome: string; regiao: { id: string; nome: string } };
  etapaAtual?: { id: string; nome: string } | null;
  ultimaVisita?: VisitaResumo | null;
}

export interface VisitaResumo {
  id: string;
  dataVisita: string;
  registradaEm: string;
  observacao?: string | null;
  etapa?: { id: string; nome: string } | null;
  inspetor?: { id: string; fullName?: string | null; username?: string | null } | null;
}

export interface RespostaDaVisita {
  itemId: string;
  valor: unknown;
  rotulo?: string;
  tipo?: ChecklistVisitaTipo;
  itemAtivo?: boolean;
}

export interface VisitaExterna extends VisitaResumo {
  previsaoEntrega?: string | null;
  previsaoEtapa?: string | null;
  respostas: RespostaDaVisita[];
  fotos: Array<{ id: string; url: string; mime: string }>;
}

export interface AtendimentoExternoDetalhe extends AtendimentoExterno {
  criadoPor?: { id: string; fullName?: string | null } | null;
  saidaPor?: { id: string; fullName?: string | null } | null;
  visitas: VisitaExterna[];
}

export interface CatalogoOficinaExterna {
  geradoEm: string;
  diasSemVisitaAlerta: number;
  regioes: RegiaoExterna[];
  oficinas: OficinaExterna[];
  etapas: EtapaExterna[];
  checklist: ChecklistVisita;
  atendimentos: AtendimentoExterno[];
}

export interface Cobertura {
  diasAlerta: number;
  referencia: string;
  oficinas: Array<{
    id: string;
    nome: string;
    regiao: { id: string; nome: string };
    atendimentosAtivos: number;
    ultimaVisitaEm: string | null;
    diasSemVisita: number | null;
    placasSemVisitaRecente: number;
  }>;
  placas: Array<{
    atendimentoId: string;
    placa: string;
    oficina: { id: string; nome: string; regiao: { id: string; nome: string } };
    ultimaVisitaEm: string | null;
    entradaEm: string;
    diasSemVisita: number | null;
  }>;
}

export interface ListaDeAtendimentos {
  itens: AtendimentoExterno[];
  total: number;
  page: number;
  limit: number;
  diasSemVisitaAlerta: number;
}

export interface FiltrosDeAtendimento {
  regiaoId?: string;
  oficinaId?: string;
  placa?: string;
  evento?: number;
  situacao?: 'NA_OFICINA' | 'SAIU' | 'TODOS';
  semVisitaHaDias?: number;
  page?: number;
  limit?: number;
}

/** O que a fila do aparelho envia; `clienteId` é a chave de idempotência (ADR 0059). */
export interface NovoAtendimentoExterno {
  clienteId: string;
  eventNumber: number;
  placa: string;
  classe: string;
  oficinaId: string;
  entradaEm: string;
  modelo?: string;
  cor?: string;
  anoModelo?: string;
}

export interface NovaVisita {
  clienteId: string;
  dataVisita: string;
  etapaId?: string;
  previsaoEntrega?: string;
  previsaoEtapa?: string;
  observacao?: string;
  templateVersao: string;
  respostas?: Array<{ itemId: string; valor: unknown }>;
}

export interface NovaSaida {
  clienteId: string;
  saidaEm: string;
  motivo?: string;
}

// ------------------------------------------------------------------- leitura

export const fetchCatalogoOficinaExterna = async (): Promise<CatalogoOficinaExterna> => {
  const { data } = await api.get('/oficina-externa/catalogo');
  return data;
};

export const fetchAtendimentosExternos = async (
  filtros: FiltrosDeAtendimento = {},
): Promise<ListaDeAtendimentos> => {
  const { data } = await api.get('/oficina-externa/atendimentos', { params: filtros });
  return data;
};

export const fetchAtendimentoExterno = async (
  id: string,
): Promise<AtendimentoExternoDetalhe> => {
  const { data } = await api.get(`/oficina-externa/atendimentos/${id}`);
  return data;
};

export const fetchAtendimentosExternosDaPlaca = async (
  placa: string,
): Promise<AtendimentoExterno[]> => {
  const { data } = await api.get(
    `/oficina-externa/atendimentos/por-placa/${encodeURIComponent(placa)}`,
  );
  return data;
};

export const fetchCoberturaOficinaExterna = async (regiaoId?: string): Promise<Cobertura> => {
  const { data } = await api.get('/oficina-externa/cobertura', {
    params: regiaoId ? { regiaoId } : undefined,
  });
  return data;
};

export const fetchRegioesExternas = async (): Promise<RegiaoExterna[]> => {
  const { data } = await api.get('/oficina-externa/regioes');
  return data;
};

export const fetchOficinasExternas = async (regiaoId?: string): Promise<OficinaExterna[]> => {
  const { data } = await api.get('/oficina-externa/oficinas', {
    params: regiaoId ? { regiaoId } : undefined,
  });
  return data;
};

// ------------------------------------------------------- escritas do inspetor

export const criarAtendimentoExterno = async (payload: NovoAtendimentoExterno) => {
  const { data } = await api.post('/oficina-externa/atendimentos', payload);
  return data as AtendimentoExterno & { jaExistia: boolean };
};

export const registrarVisitaExterna = async (atendimentoId: string, payload: NovaVisita) => {
  const { data } = await api.post(
    `/oficina-externa/atendimentos/${atendimentoId}/visitas`,
    payload,
  );
  return data as { id: string; atendimentoId: string; jaExistia: boolean };
};

/**
 * Uma foto por requisição (o mesmo desenho do checklist). O timeout é longo porque quem envia
 * está em rede móvel na estrada, e curto demais transformaria lentidão em item RECUSADO.
 */
export const enviarFotoDaVisita = async (
  visitaId: string,
  arquivo: Blob,
  clienteId: string,
  nome = `${clienteId}.jpg`,
) => {
  const form = new FormData();
  form.append('file', arquivo, nome);
  form.append('clienteId', clienteId);

  const { data } = await api.post(`/oficina-externa/visitas/${visitaId}/fotos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return data as { id: string; url: string; mime: string; jaExistia: boolean };
};

/** at-132: o teto do vídeo da visita (o mesmo do servidor) e o do envio pelo backend. */
export const VIDEO_DA_VISITA_MAX_BYTES = 100 * 1024 * 1024;
const ENVIO_PELO_BACKEND_MAX_BYTES = 30 * 1024 * 1024;

/** Erro que a fila trata como PENDENTE (não recusa): reenviar mais tarde resolve. */
const pendente = (mensagem: string) => {
  const erro: any = new Error(mensagem);
  erro.response = { status: 503 };
  return erro;
};

/**
 * at-132 — o vídeo da visita, em três passos (molde do checklist, at-29/at-33):
 * 1. `prepare-upload` devolve a URL assinada (ou `jaExistia`, se o vídeo já foi registrado);
 * 2. `PUT` direto ao bucket — o vídeo não passa pelo backend, que tem teto de ~32 MB por request;
 * 3. `confirm-upload` registra o vídeo na visita, depois de o servidor conferir no bucket.
 *
 * 🔴 Falha do PUT nunca vira RECUSA: se a rede bloqueia o bucket e o vídeo cabe em 30 MB, ele vai
 * pelo envio das fotos (fallback da at-33); senão a fila o mantém pendente para tentar depois. O
 * mesmo para o confirm que não acha o objeto — o vídeo continua no aparelho e é reenviado.
 */
export const enviarVideoDaVisita = async (
  visitaId: string,
  arquivo: Blob,
  clienteId: string,
  nome: string,
): Promise<{ id: string; url?: string; mime?: string; jaExistia: boolean }> => {
  const mime = arquivo.type || 'video/mp4';
  const { data: preparado } = await api.post(
    `/oficina-externa/visitas/${visitaId}/videos/prepare-upload`,
    { clienteId, nomeArquivo: nome, mime, tamanhoBytes: arquivo.size },
  );
  if (preparado.jaExistia) return preparado;

  try {
    // Axios cru, SEM o interceptor de autenticação: a URL assinada já é a credencial, e um
    // header Authorization a mais faria o bucket recusar a assinatura.
    await axios.put(preparado.uploadUrl, arquivo, {
      headers: { 'Content-Type': mime },
      timeout: 600000,
    });
  } catch (erroDoPut: any) {
    if (!erroDoPut?.response && arquivo.size <= ENVIO_PELO_BACKEND_MAX_BYTES) {
      return enviarFotoDaVisita(visitaId, arquivo, clienteId, nome);
    }
    throw pendente('O vídeo ainda não pôde ser enviado. Ele continua guardado no aparelho.');
  }

  try {
    const { data } = await api.post(`/oficina-externa/visitas/${visitaId}/videos/confirm-upload`, {
      clienteId,
      storageKey: preparado.storageKey,
    });
    return data;
  } catch (erro: any) {
    const code = erro?.response?.data?.code ?? erro?.response?.data?.message?.code;
    if (code === 'VIDEO_NAO_ENCONTRADO') {
      throw pendente('O vídeo não chegou ao armazenamento. Ele será enviado de novo.');
    }
    throw erro;
  }
};

export const registrarSaidaExterna = async (atendimentoId: string, payload: NovaSaida) => {
  const { data } = await api.post(
    `/oficina-externa/atendimentos/${atendimentoId}/saida`,
    payload,
  );
  return data as { id: string; saidaEm: string; jaExistia: boolean };
};

// ------------------------------------------------------------- configuração

export const fetchConfigRegioes = async (): Promise<RegiaoExterna[]> => {
  const { data } = await api.get('/oficina-externa/config/regioes');
  return data;
};

export const criarRegiaoExterna = async (nome: string): Promise<RegiaoExterna> => {
  const { data } = await api.post('/oficina-externa/config/regioes', { nome });
  return data;
};

export const atualizarRegiaoExterna = async (
  id: string,
  payload: { nome?: string; ativa?: boolean; ordem?: number },
): Promise<RegiaoExterna> => {
  const { data } = await api.patch(`/oficina-externa/config/regioes/${id}`, payload);
  return data;
};

export const desativarRegiaoExterna = async (id: string) => {
  const { data } = await api.delete(`/oficina-externa/config/regioes/${id}`);
  return data;
};

export const reordenarRegioesExternas = async (ids: string[]) => {
  const { data } = await api.put('/oficina-externa/config/regioes/reorder', { ids });
  return data as RegiaoExterna[];
};

export const fetchConfigOficinas = async (regiaoId?: string): Promise<OficinaExterna[]> => {
  const { data } = await api.get('/oficina-externa/config/oficinas', {
    params: regiaoId ? { regiaoId } : undefined,
  });
  return data;
};

export const criarOficinaExterna = async (payload: {
  regiaoId: string;
  nome: string;
  endereco?: string;
  telefone?: string;
  contato?: string;
  observacoes?: string;
}): Promise<OficinaExterna> => {
  const { data } = await api.post('/oficina-externa/config/oficinas', payload);
  return data;
};

export const atualizarOficinaExterna = async (
  id: string,
  payload: Partial<{
    regiaoId: string;
    nome: string;
    endereco: string;
    telefone: string;
    contato: string;
    observacoes: string;
    ativa: boolean;
  }>,
): Promise<OficinaExterna> => {
  const { data } = await api.patch(`/oficina-externa/config/oficinas/${id}`, payload);
  return data;
};

export const desativarOficinaExterna = async (id: string) => {
  const { data } = await api.delete(`/oficina-externa/config/oficinas/${id}`);
  return data;
};

export const fetchConfigEtapas = async (): Promise<EtapaExterna[]> => {
  const { data } = await api.get('/oficina-externa/config/etapas');
  return data;
};

export const criarEtapaExterna = async (nome: string): Promise<EtapaExterna> => {
  const { data } = await api.post('/oficina-externa/config/etapas', { nome });
  return data;
};

export const atualizarEtapaExterna = async (
  id: string,
  payload: { nome?: string; ativa?: boolean },
): Promise<EtapaExterna> => {
  const { data } = await api.patch(`/oficina-externa/config/etapas/${id}`, payload);
  return data;
};

export const desativarEtapaExterna = async (id: string) => {
  const { data } = await api.delete(`/oficina-externa/config/etapas/${id}`);
  return data;
};

export const reordenarEtapasExternas = async (ids: string[]) => {
  const { data } = await api.put('/oficina-externa/config/etapas/reorder', { ids });
  return data as EtapaExterna[];
};

export const fetchConfigChecklist = async (): Promise<ChecklistVisita> => {
  const { data } = await api.get('/oficina-externa/config/checklist');
  return data;
};

export const criarItemDoChecklist = async (payload: {
  rotulo: string;
  tipo: ChecklistVisitaTipo;
  obrigatorio?: boolean;
  opcoes?: string[];
}): Promise<ChecklistVisitaItem> => {
  const { data } = await api.post('/oficina-externa/config/checklist/itens', payload);
  return data;
};

export const atualizarItemDoChecklist = async (
  id: string,
  payload: Partial<{ rotulo: string; obrigatorio: boolean; opcoes: string[]; ativo: boolean }>,
): Promise<ChecklistVisitaItem> => {
  const { data } = await api.patch(`/oficina-externa/config/checklist/itens/${id}`, payload);
  return data;
};

export const desativarItemDoChecklist = async (id: string) => {
  const { data } = await api.delete(`/oficina-externa/config/checklist/itens/${id}`);
  return data;
};

export const reordenarChecklistDaVisita = async (ids: string[]) => {
  const { data } = await api.put('/oficina-externa/config/checklist/itens/reorder', { ids });
  return data as ChecklistVisita;
};

export interface ParametrosOficinaExterna {
  diasSemVisitaAlerta: number;
  alteradoPor?: { id: string; fullName?: string | null; username?: string | null } | null;
  alteradoEm?: string | null;
  padraoDoSistema: boolean;
}

export const fetchParametrosOficinaExterna = async (): Promise<ParametrosOficinaExterna> => {
  const { data } = await api.get('/oficina-externa/config/parametros');
  return data;
};

export const salvarParametrosOficinaExterna = async (
  diasSemVisitaAlerta: number,
): Promise<ParametrosOficinaExterna> => {
  const { data } = await api.put('/oficina-externa/config/parametros', { diasSemVisitaAlerta });
  return data;
};

/** at-132: quantos veículos geraram aviso e para quantas pessoas. */
export interface ResultadoDoAvisoSemVisita {
  veiculos: number;
  destinatarios: number;
}

/**
 * at-132: dispara agora o aviso de "sem visita" — o mesmo das 08h. Repetir não repete aviso: só
 * sai para veículo que ainda não foi avisado desde a última visita.
 */
export const dispararAvisoSemVisita = async (): Promise<ResultadoDoAvisoSemVisita> => {
  const { data } = await api.post('/oficina-externa/config/avisos-sem-visita/executar');
  return data;
};

/**
 * Uma visita do PRÓPRIO inspetor, na aba "Minhas visitas" (25/09/2026).
 *
 * ⚠️ `totalFotos` é contagem, não lista: as URLs assinadas só saem no detalhe do veículo
 * (`fetchAtendimentoExterno`), que é o que o botão "Ver veículo" abre.
 */
export interface MinhaVisita {
  id: string;
  dataVisita: string;
  registradaEm: string;
  previsaoEntrega?: string | null;
  previsaoEtapa?: string | null;
  observacao?: string | null;
  etapa?: { id: string; nome: string } | null;
  totalFotos: number;
  respostas: RespostaDaVisita[];
  atendimento: {
    id: string;
    placa: string;
    modelo?: string | null;
    saidaEm?: string | null;
    evento?: { eventNumber: number } | null;
    oficina?: { id: string; nome: string; regiao?: { id: string; nome: string } | null } | null;
  };
}

export interface ListaDeMinhasVisitas {
  itens: MinhaVisita[];
  total: number;
  page: number;
  limit: number;
}

export const fetchMinhasVisitas = async (
  filtros: { placa?: string; page?: number; limit?: number } = {},
): Promise<ListaDeMinhasVisitas> => {
  const { data } = await api.get('/oficina-externa/minhas-visitas', { params: filtros });
  return data;
};

// ------------------------------------------------------------- relatório (at-132)

export type ModoRelatorioExterno = 'VISITAS' | 'VEICULOS';
export type FormatoRelatorioExterno = 'CSV' | 'PDF';

export interface ModoDoRelatorioExterno {
  id: ModoRelatorioExterno;
  titulo: string;
  /** O que UMA LINHA representa — exibido na tela, para ninguém somar o que não se soma. */
  granularidade: string;
  descricao: string;
  padrao: string[];
  colunas: Array<{ id: string; titulo: string; secao: string }>;
}

export interface OpcoesDoRelatorioExterno {
  modoPadrao: ModoRelatorioExterno;
  maxColunasPdf: number;
  modos: ModoDoRelatorioExterno[];
}

export interface FiltrosDoRelatorioExterno {
  /** Datas PURAS (`YYYY-MM-DD`), enviadas como o campo devolve — nunca por `toISOString`. */
  de?: string;
  ate?: string;
  regiaoId?: string;
  oficinaId?: string;
  placa?: string;
  evento?: string;
  situacao?: 'NA_OFICINA' | 'SAIU' | 'TODOS';
}

/**
 * O catálogo do relatório (inclui uma coluna por pergunta do checklist). Fonte única, como no
 * Relatório de Veículos (at-107): o cardápio nunca é escrito no frontend.
 */
export const fetchOpcoesDoRelatorioExterno = async (): Promise<OpcoesDoRelatorioExterno> => {
  const { data } = await api.get('/oficina-externa/relatorio/opcoes');
  return data;
};

/** Gera o arquivo e devolve o blob com o nome que o servidor mandou. */
export const gerarRelatorioExterno = async (opcoes: {
  colunas: string[];
  modo: ModoRelatorioExterno;
  formato: FormatoRelatorioExterno;
  filtros: FiltrosDoRelatorioExterno;
}): Promise<{ arquivo: Blob; nome: string }> => {
  const params: Record<string, string> = {
    colunas: opcoes.colunas.join(','),
    modo: opcoes.modo,
    formato: opcoes.formato,
  };
  Object.entries(opcoes.filtros).forEach(([chave, valor]) => {
    if (valor !== undefined && `${valor}`.trim() !== '') params[chave] = `${valor}`.trim();
  });

  const resposta = await api.get('/oficina-externa/relatorio', { params, responseType: 'blob' });
  const cabecalho = String(resposta.headers?.['content-disposition'] ?? '');
  const casado = cabecalho.match(/filename="?([^"]+)"?/i);
  return {
    arquivo: resposta.data,
    nome: casado?.[1] ?? `relatorio_oficina_externa.${opcoes.formato.toLowerCase()}`,
  };
};
